import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ComboboxDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    ChipSizeConstantType,
    ChipSizeType
} from "../chip/type";
import {
    getChipSize
} from "../chip/stylesheet";
import {
    webStyle
} from "../../utils";

export const COMBOBOX_CHIP_SIZE: ChipSizeType = "small";

export const getComboboxChipHeight = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    iconSizes
}: Omit<ChipSizeConstantType, "size">): number => {
    return getChipSize({
        size: COMBOBOX_CHIP_SIZE,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes
    }).minHeight;
};

const stylesheet = StyleSheet.create({
    readOnlyInput: {
        backgroundColor: "transparent",
        textAlignVertical: "center",
        includeFontPadding: false,
        alignSelf: "stretch",
        borderWidth: 0,
        minWidth: 0,
        padding: 0,
        margin: 0,
        flex: 1
    },
    searchInput: {
        backgroundColor: "transparent",
        textAlignVertical: "center",
        includeFontPadding: false,
        borderWidth: 0,
        minWidth: 0,
        padding: 0,
        margin: 0,
        flex: 1
    },
    trigger: {
        ...StyleSheet.absoluteFill,
        ...webStyle({
            outlineStyle: "solid",
            cursor: "pointer",
            outlineWidth: 0
        })
    },
    content: {
        pointerEvents: "box-none",
        flexDirection: "row",
        alignItems: "center",
        minWidth: 0,
        flex: 1
    },
    loadingTrigger: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    chevron: {
        justifyContent: "center",
        alignItems: "center"
    },
    measure: {
        pointerEvents: "none",
        flexDirection: "row",
        position: "absolute",
        alignItems: "center",
        opacity: 0,
        start: 0,
        top: 0
    },
    live: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        opacity: 0
    },
    anchor: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "stretch",
        position: "relative",
        minWidth: 0,
        flex: 1
    },
    chips: {
        flexDirection: "row",
        alignItems: "center",
        overflow: "hidden",
        minWidth: 0,
        flex: 1
    },
    leavingChip: {
        pointerEvents: "none"
    },
    tabular: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    inlineChips: {
        flexBasis: "auto",
        flexShrink: 0,
        flexGrow: 0
    },
    value: {
        flexShrink: 1
    },
    measureItem: {
        flexShrink: 0
    }
});

export const useStyles = ({
    currentSize,
    chipHeight,
    isMultiple,
    isLoading,
    borders,
    spaces
}: ComboboxDynamicStyleType) => {
    const styles = {
        searchInput: {
            minWidth: isMultiple ? currentSize.minHeight * 2 : 0
        } as Mutable<TextStyle>,
        chevron: {
            height: currentSize.iconSize,
            width: currentSize.iconSize
        } as Mutable<ViewStyle>,
        anchor: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        chips: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        measure: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        live: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>,
        trigger: {
        } as Mutable<ViewStyle>
    };

    if(isMultiple) {
        styles.anchor.marginStart = (currentSize.minHeight - chipHeight) / 2 - currentSize.paddingHorizontal - borders.line;
    }

    if(isLoading) {
        Object.assign(styles.trigger, stylesheet.loadingTrigger);
    }

    return styles;
};
export default stylesheet;
